<template>
  <div class="production-pipeline" :class="{ compact }">
    <div class="pipeline-track">
      <div
        v-for="(stage, idx) in stageList"
        :key="stage.key"
        class="pipeline-stage"
        :class="{ clickable: !compact }"
        @click="!compact && $emit('select-stage', stage.key)"
      >
        <!-- 연결선 -->
        <div v-if="idx > 0" class="connector" :class="getConnectorClass(idx)" />

        <!-- 원형 -->
        <el-tooltip
          :content="getTooltip(stage)"
          :disabled="!compact"
          placement="top"
          :show-after="300"
        >
          <div class="stage-circle" :class="getStageClass(stage.key)">
            <span v-if="!compact" class="stage-icon">{{ stage.icon }}</span>
          </div>
        </el-tooltip>

        <!-- 라벨 (compact=false) -->
        <div v-if="!compact" class="stage-label" :class="getStageClass(stage.key)">
          {{ stage.label }}
        </div>

        <!-- 진행률 (compact=false) -->
        <div v-if="!compact && getDetail(stage.key)" class="stage-detail">
          {{ getDetail(stage.key) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  stages: { type: Object, default: () => ({}) },
  compact: { type: Boolean, default: true },
})

defineEmits(['select-stage'])

const stageList = [
  { key: 'work_order', label: '작업지시', icon: '📝' },
  { key: 'material_procurement', label: '자재조달', icon: '📦' },
  { key: 'material_export', label: '자재수출', icon: '🚢' },
  { key: 'production', label: '생산', icon: '🏭' },
  { key: 'product_import', label: '제품수입', icon: '📥' },
  { key: 'delivery', label: '납품', icon: '✅' },
]

const getStatus = (key) => props.stages[key]?.status || 'none'

const getStageClass = (key) => {
  const s = getStatus(key)
  if (s === 'completed') return 'completed'
  if (s === 'in_progress') return 'active'
  if (s === 'pending') return 'pending'
  return 'none'
}

const getConnectorClass = (idx) => {
  const prevKey = stageList[idx - 1].key
  const prevStatus = getStatus(prevKey)
  if (prevStatus === 'completed') return 'completed'
  if (prevStatus === 'in_progress') return 'active'
  return 'pending'
}

const getDetail = (key) => {
  const stage = props.stages[key]
  if (!stage) return null
  return stage.detail || null
}

const getTooltip = (stage) => {
  const data = props.stages[stage.key]
  if (!data) return stage.label
  const statusText = {
    completed: '완료',
    in_progress: '진행중',
    pending: '대기',
    none: '-',
  }[data.status] || '-'
  const detail = data.detail ? ` (${data.detail})` : ''
  return `${stage.label}: ${statusText}${detail}`
}
</script>

<style scoped>
.production-pipeline {
  padding: 16px 8px 12px;
}

.production-pipeline.compact {
  padding: 4px 0;
}

.pipeline-track {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
}

.pipeline-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 0;
}

.pipeline-stage.clickable {
  cursor: pointer;
}

.pipeline-stage.clickable:hover .stage-circle {
  transform: scale(1.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* 연결선 */
.connector {
  position: absolute;
  top: 10px;
  right: 50%;
  width: 100%;
  height: 3px;
  background: #e4e7ed;
  z-index: 0;
}

.compact .connector {
  top: 6px;
  height: 2px;
}

.connector.completed {
  background: #67C23A;
}

.connector.active {
  background: linear-gradient(90deg, #67C23A, #409EFF);
}

/* 원형 */
.stage-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e4e7ed;
  border: 2px solid #dcdfe6;
  z-index: 1;
  transition: all 0.2s;
  position: relative;
}

.compact .stage-circle {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

.stage-circle.completed {
  background: #67C23A;
  border-color: #67C23A;
}

.stage-circle.active {
  background: #409EFF;
  border-color: #409EFF;
  animation: pulse 2s infinite;
}

.stage-circle.pending {
  background: #fff;
  border-color: #dcdfe6;
}

.stage-circle.none {
  background: #fff;
  border-color: #e4e7ed;
  border-style: dashed;
}

.stage-icon {
  font-size: 10px;
  line-height: 1;
}

/* 라벨 */
.stage-label {
  margin-top: 4px;
  font-size: 11px;
  color: #909399;
  white-space: nowrap;
  font-weight: 500;
}

.stage-label.completed {
  color: #67C23A;
  font-weight: 600;
}

.stage-label.active {
  color: #409EFF;
  font-weight: 600;
}

/* 진행률 */
.stage-detail {
  font-size: 10px;
  color: #C0C4CC;
  margin-top: 1px;
}

@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(64, 158, 255, 0); }
}
</style>
